import { Box, Stack, Typography, Autocomplete, TextField, Chip, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import type { MultiTimeframeEntry } from '~/state/useMultiTimeframeState'
import { CONTROLS, CARDS } from '~/components/Charts/chrome/chartChrome'

// NOTE: Rendered by ReviewPageScaffold on multi-timeframe pages (multiTimeframe state non-null).
// Prefer the canonical `AbsoluteTimeframe` type from `./TimeframeSelect` over this
// structurally-identical `CampaignOption` if the type is ever unified.
export interface CampaignOption {
  value: string
  label: string
  from: string
  to: string
  /** Campaigns C1 Task 6: present for campaign-sourced options — carried onto the added
   *  entry as MultiTimeframeEntry.campaign (id presence = campaign entry). */
  campaign?: string
  /** Whether the campaign carries a non-empty attribution predicate (Task 7 resolves it
   *  per-arm). Informational only here — the chip badge keys off the entry's campaign id. */
  hasPredicate?: boolean
}

interface Props {
  entries: MultiTimeframeEntry[]
  options: CampaignOption[]
  onAdd: (e: MultiTimeframeEntry) => void
  onRemove: (i: number) => void
}

export function MultiTimeframePicker({ entries, options, onAdd, onRemove }: Props) {
  return (
    <Box>
      <Autocomplete
        sx={{ minWidth: 320, mb: 2 }}
        options={options}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        value={null}
        onChange={(_, opt) => {
          if (opt)
            onAdd({
              label: opt.label,
              from: opt.from,
              to: opt.to,
              ...(opt.campaign ? { campaign: opt.campaign } : {}),
            })
        }}
        renderInput={(params) => <TextField {...params} label={CONTROLS.addTimeframe} />}
      />

      {entries.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {CARDS.useMainTimeframeHint}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {entries.map((e, i) => (
            <Stack key={`${e.label}-${i}`} direction="row" alignItems="center" spacing={1}>
              <Chip
                label={
                  e.campaign ? (
                    <>
                      {e.label}
                      <FilterAltOutlinedIcon
                        data-testid="multi-tf-campaign-badge"
                        fontSize="inherit"
                        aria-label={CONTROLS.campaignHasFilters}
                        sx={{ ml: 0.5, verticalAlign: 'middle' }}
                      />
                    </>
                  ) : (
                    e.label
                  )
                }
              />
              <Typography variant="body2" color="text.secondary">
                {e.from} → {e.to}
              </Typography>
              <IconButton size="small" aria-label={CONTROLS.removeAria} onClick={() => onRemove(i)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  )
}
